<template>
  <div class="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
    <div class="w-full max-w-sm space-y-10">
      <div v-if="!success">
        <img class="mx-auto h-30 w-auto dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <img class="mx-auto h-30 w-auto not-dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <h2
          class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white"
        >
          Create your account
        </h2>
      </div>

      <form class="space-y-6" novalidate @submit.prevent="onSubmit">
        <div v-if="!success">
          <div class="col-span-2 relative">
            <input
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              required
              autofocus
              aria-label="Email"
              class="block w-full rounded-t-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Email (will be your username)"
              v-model.trim="username"
              @blur="showEmailError = false"
              @focus="showEmailError = true"
              :disabled="loading"
            />
            <!-- Submit-only clickable error icon for email -->
            <button
              v-if="submitAttempted && !isUsernameValid && !showEmailError"
              type="button"
              class="absolute pointer-events-none right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-600 shadow-sm"
              aria-label="Email is invalid. Click to view details"
            >
              !
            </button>
            <div
              v-if="submitAttempted && !isUsernameValid && showEmailError"
              class="absolute left-full top-1/2 -translate-y-1/2 ml-2 right-0 mt-1 w-64 rounded-md border border-red-300 bg-white p-2 text-xs text-red-700 shadow-lg dark:bg-gray-900 dark:text-red-400 dark:border-red-700"
              role="alert"
            >
              Please enter a valid email like user@example.com
            </div>
          </div>
          <div class="-mt-px relative">
            <input
              id="givenName"
              name="givenName"
              type="text"
              autocomplete="given-name"
              required
              aria-label="Given name"
              class="block w-full bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Given name"
              v-model.trim="givenName"
              @blur="showGivenNameError = false"
              @focus="showGivenNameError = true"
              :disabled="loading"
            />
            <button
              v-if="submitAttempted && !givenName && !showGivenNameError"
              type="button"
              class="absolute pointer-events-none right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-600 shadow-sm"
              aria-label="Email is invalid. Click to view details"
            >
              !
            </button>
            <div
              v-if="submitAttempted && !givenName && showGivenNameError"
              class="absolute left-full top-1/2 -translate-y-1/2 ml-2 right-0 mt-1 w-64 rounded-md border border-red-300 bg-white p-2 text-xs text-red-700 shadow-lg dark:bg-gray-900 dark:text-red-400 dark:border-red-700"
              role="alert"
            >
              What's your name?
            </div>
          </div>
          <div class="-mt-px relative">
            <input
              id="familyName"
              name="familyName"
              type="text"
              autocomplete="family-name"
              required
              aria-label="Family name"
              class="block w-full bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Family name"
              v-model.trim="familyName"
              :disabled="loading"
              @blur="showFamilyNameError = false"
              @focus="showFamilyNameError = true"
            />
            <button
              v-if="submitAttempted && !familyName && !showFamilyNameError"
              type="button"
              class="absolute pointer-events-none right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-600 shadow-sm"
              aria-label="Email is invalid. Click to view details"
            >
              !
            </button>
            <div
              v-if="submitAttempted && !familyName && showFamilyNameError"
              class="absolute left-full top-1/2 -translate-y-1/2 ml-2 right-0 mt-1 w-64 rounded-md border border-red-300 bg-white p-2 text-xs text-red-700 shadow-lg dark:bg-gray-900 dark:text-red-400 dark:border-red-700"
              role="alert"
            >
              What's your name?
            </div>
          </div>
          <div class="-mt-px relative">
            <input
              id="password"
              name="password"
              type="password"
              autocomplete="new-password"
              required
              aria-label="Password"
              class="block w-full bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              placeholder="Password"
              v-model="password"
              :disabled="loading"
              @blur="showPasswordError = false"
              @focus="showPasswordError = true"
            />

            <button
              v-if="submitAttempted && !isPasswordValid && !showPasswordError"
              type="button"
              class="absolute pointer-events-none right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-600 shadow-sm"
              aria-label="Password does not meet requirements. Click to view details"
            >
              !
            </button>
            <div
              v-if="submitAttempted && !isPasswordValid && showPasswordError"
              class="absolute left-full top-1/2 -translate-y-1/2 ml-2 right-0 mt-1 w-72 rounded-md border border-red-300 bg-white p-2 text-xs text-red-700 shadow-lg dark:bg-gray-900 dark:text-red-400 dark:border-red-700"
              role="alert"
            >
              Password must be at least 8 characters and include uppercase, lowercase, number, and
              symbol.
            </div>
          </div>
          <div class="-mt-px relative">
            <input
              id="verifyPassword"
              name="verifyPassword"
              type="password"
              autocomplete="new-password"
              required
              aria-label="Verify password"
              class="block w-full rounded-b-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              placeholder="Verify password"
              v-model="verifyPassword"
              :disabled="loading"
              @blur="showVerifyPasswordError = false"
              @focus="showVerifyPasswordError = true"
            />

            <!-- Submit-only clickable error icon for password match -->
            <button
              v-if="
                submitAttempted &&
                password &&
                verifyPassword &&
                !passwordsMatch &&
                !showVerifyPasswordError
              "
              type="button"
              class="absolute pointer-events-none right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-600 shadow-sm"
              aria-label="Passwords do not match. Click to view details"
            >
              !
            </button>
            <div
              v-if="
                submitAttempted &&
                password &&
                verifyPassword &&
                !passwordsMatch &&
                showVerifyPasswordError
              "
              class="absolute left-full top-1/2 -translate-y-1/2 ml-2 right-0 mt-1 w-64 rounded-md border border-red-300 bg-white p-2 text-xs text-red-700 shadow-lg dark:bg-gray-900 dark:text-red-400 dark:border-red-700"
              role="alert"
            >
              Passwords do not match.
            </div>
          </div>
        </div>

        <div class="space-y-2">
          <button
            v-if="!success"
            type="submit"
            :disabled="loading"
            class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
          >
            <span v-if="loading">Creating account…</span>
            <span v-else>Create account</span>
          </button>
          <p v-if="error" class="text-sm text-red-600 dark:text-red-400" role="alert">
            {{ error }}
          </p>
          <div v-if="success" class="rounded-md border border-gray-200 p-4 dark:border-white/10">
            <p class="text-sm text-green-700 dark:text-green-400">Registration successful.</p>
            <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">
              We emailed you a 6-digit code at <strong class="break-all">{{ username }}</strong>.
              Enter it in the dashboard to verify your email; it's needed before you choose a plan.
              The code is valid for 30 minutes, and you can request a new one there.
            </p>
            <div v-if="otpAuthUrl" class="mt-3 space-y-2">
              <p class="text-sm text-gray-700 dark:text-gray-300">
                Scan this QR code with your authenticator app, or use the link below:
              </p>
              <img
                v-if="qrCodeDataUrl"
                :src="qrCodeDataUrl"
                alt="2FA QR code"
                class="mx-auto h-60 w-60"
              />
              <div
                v-else
                class="mx-auto h-60 w-60 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
              />
              <p class="break-all text-xs text-gray-500 dark:text-gray-400">{{ otpAuthUrl }}</p>

              <!-- Inline verification form after registration -->
              <div class="mt-4">
                <label for="otp" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
                  >Enter 6-digit code to finish sign-in</label
                >
                <div class="mt-2 flex gap-2">
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputmode="numeric"
                    pattern="\\d{6}"
                    maxlength="6"
                    autocomplete="one-time-code"
                    required
                    aria-label="One-time code"
                    class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
                    placeholder="6-digit code"
                    v-model="otp"
                    :disabled="verifying"
                    @keydown.enter.prevent="onVerify"
                  />
                  <button
                    type="button"
                    class="shrink-0 rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    @click="onVerify"
                    :disabled="verifying || !otp"
                  >
                    <span v-if="verifying">Verifying…</span>
                    <span v-else>Verify & Continue</span>
                  </button>
                </div>
                <p
                  v-if="verifyError"
                  class="mt-2 text-sm text-red-600 dark:text-red-400"
                  role="alert"
                >
                  {{ verifyError }}
                </p>
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  We will sign you in automatically using the password you just set.
                </p>
              </div>
            </div>
            <div v-else class="mt-2 text-sm text-gray-700 dark:text-gray-300">
              You can now sign in.
            </div>
          </div>
        </div>
      </form>

      <p v-if="!success" class="text-center text-sm/6 text-gray-500 dark:text-gray-400">
        Already have an account?
        {{ ' ' }}
        <router-link
          :to="{ name: 'Login', query: returnQuery }"
          class="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >Sign in</router-link
        >
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { redirectTarget } from '@/lib/redirect';
import { useAuthStore } from '@/stores/auth';
import QRCode from 'qrcode';

onMounted(() => {
  let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', `${window.location.origin}/register`);
});

onUnmounted(() => {
  const link = document.querySelector('link[rel="canonical"]');
  if (link) {
    link.remove();
  }
});

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;

const router = useRouter();
const route = useRoute();
const auth = useAuthStore();
const returnTo = redirectTarget(route.query);
const returnQuery = returnTo ? { redirect: returnTo } : {};

const username = ref('');
const givenName = ref('');
const familyName = ref('');
const password = ref('');
const verifyPassword = ref('');
const loading = ref(false);
const error = ref<string | null>(null);
const success = ref(false);
const otpAuthUrl = ref<string | null>(null);

// Inline verification state after successful registration
const otp = ref('');
const verifying = ref(false);
const verifyError = ref<string | null>(null);

// Submit-only error handling
const submitAttempted = ref(false);
const showEmailError = ref(false);
const showFamilyNameError = ref(false);
const showGivenNameError = ref(false);
const showPasswordError = ref(false);
const showVerifyPasswordError = ref(false);

interface RequestError {
  message?: string;
  response?: { data?: { message?: string } };
}

const isUsernameValid = computed(() => /.+@.+/.test(username.value));
const passwordsMatch = computed(() => password.value === verifyPassword.value);
const isPasswordValid = computed(() => checkPassword(password.value));
const qrCodeDataUrl = ref<string | null>(null);

const formValid = computed(
  () =>
    Boolean(
      username.value &&
      givenName.value &&
      familyName.value &&
      password.value &&
      verifyPassword.value
    ) &&
    isUsernameValid.value &&
    passwordsMatch.value &&
    isPasswordValid.value
);
function checkPassword(password: string): boolean {
  if (!password) return false;
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  return hasMinLength && hasUppercase && hasLowercase && hasDigit && hasSymbol;
}
async function generateQrDataUrl(text: string): Promise<string> {
  if (!text) throw new Error('QR code text cannot be empty');

  // QRCode.toDataURL returns a Promise, which is why we must use 'await'
  return QRCode.toDataURL(text, {
    margin: 1, // Small margin
    width: 240, // Matches your 'w-60' (240px)
  });
}

async function onSubmit() {
  submitAttempted.value = true;
  // If invalid, show submit-only icons and stop here
  if (!formValid.value) {
    return;
  }
  loading.value = true;
  error.value = null;
  success.value = false;
  otpAuthUrl.value = null;
  try {
    await auth.register({
      username: username.value,
      password: password.value,
      givenName: givenName.value,
      familyName: familyName.value,
    });
    // Backend returns the provisioning URI for TOTP
    otpAuthUrl.value = auth?.otpAuthUrl ?? null;
    success.value = true;
    // Keep user on page to scan QR and then proceed to login where they will complete 2FA
  } catch (failure) {
    const e = failure as RequestError | null;
    error.value = e?.response?.data?.message || e?.message || 'Registration failed';
  } finally {
    loading.value = false;
  }
}

async function onVerify() {
  if (!otp.value) return;
  verifying.value = true;
  verifyError.value = null;
  try {
    // Step 1: sign in with the credentials just created to obtain a token
    await auth.login({ username: username.value, password: password.value });
    // Step 2: if MFA is required (expected right after registration), verify the code
    if (auth.mfaRequired) {
      await auth.verifyMfa(otp.value);
    }
    if (auth.isAuthenticated) {
      // Clean up sensitive inputs and go to Dashboard
      otp.value = '';
      password.value = '';
      verifyPassword.value = '';
      // Navigate to Dashboard with a one-time query flag to show success notification
      await router.push(returnTo ?? { name: 'Dashboard', query: { registered: '1' } });
    } else {
      verifyError.value = 'Verification failed. Please try again.';
    }
  } catch (failure) {
    const e = failure as RequestError | null;
    verifyError.value =
      auth.error || e?.response?.data?.message || e?.message || 'Failed to verify';
  } finally {
    verifying.value = false;
  }
}

watch(otpAuthUrl, async (newUrl) => {
  if (newUrl) {
    try {
      // Generate the new QR code and set it
      qrCodeDataUrl.value = await generateQrDataUrl(newUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      qrCodeDataUrl.value = null; // Clear on error
    }
  } else {
    // If the URL is null, clear the image
    qrCodeDataUrl.value = null;
  }
});
</script>
